import { Formik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Eye, Edit2, Trash2, Link, Plus, Users, AlertCircle, ExternalLink } from "react-feather";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { listOfCompanyApi } from "../../Api/companyApi";
import {
  CreateContactApi,
  DeleteContactApi,
  EditContactApi,
  listOfContactsApi,
  listOfSalesPosition,
} from "../../Api/contactApi";
import BaseButton from "../../BaseComponents/BaseButton";
import BaseInput from "../../BaseComponents/BaseInput";
import BaseModal from "../../BaseComponents/BaseModal";
import BaseSelect from "../../BaseComponents/BaseSelect";
import TableContainer from "../../BaseComponents/BaseTable";
import BaseTooltip from "../../BaseComponents/BaseTooltip";
import ActionDropdown from "../../BaseComponents/BaseActionDropdown";
import { Address, Delete } from "../../Constant";
import { checkStatusCodeSuccess } from "../../Constant/common";
import { truncateText } from "../../Constant/Lead";
import { Contact_Link, Contactlink, NewTab, Remarks } from "../../Constant/Company";
import { SrNo } from "../../Constant/Center";
import { useTableQueryParams } from "../../utils/tableUtils";
import {
  Actions,
  AddContact,
  Company,
  Company_Id,
  Company_Name,
  CompanyKey,
  CompanyLabel,
  Contact_Location,
  Contact_Name,
  Contact_No,
  Contact_Remarks,
  ContactHeader,
  ContactNo,
  ContactNumPlaceholder,
  ContactPlaceholder,
  Contacts,
  DeleteConfirmation,
  email,
  Email,
  EmailPlaceholder,
  Name,
  Position,
  Position_id,
  SearchContact,
  Submit,
  Update,
  UpdateContact,
} from "../../Constant/Contact";
import { emailRegex, InputPlaceHolder, validationMessages } from "../../Constant/validation";
import { contactsTitle } from "../../Constant/title";
import { usePageLoader } from "../../_helper/loading/LoadingContext";

const ContactValidationSchema = Yup.object().shape({
  company_id: Yup.string().required(validationMessages.required(Company)),
  contact_name: Yup.string().required(validationMessages.required(Name)),
  email: Yup.string().matches(emailRegex, validationMessages.phoneNumber(EmailPlaceholder)),
  contact_no: Yup.string()
    .matches(/^\+\d{7,15}$/, validationMessages.phoneNumber(ContactNumPlaceholder))
    .min(8, validationMessages.minLength(ContactNumPlaceholder, 8))
    .max(16, validationMessages.maxLength(ContactNumPlaceholder, 16)),
  contact_link: Yup.string().url(validationMessages.url(Contactlink.toLowerCase())),
});

const EMPTY_FORM = {
  company_id: "",
  contact_name: "",
  email: "",
  contact_no: "",
  contact_location: "",
  position_id: "",
  contact_link: "",
  remarks: "",
};

const Contact = () => {
  useEffect(() => {
    document.title = contactsTitle;
  }, []);

  const { setLoading } = usePageLoader();

  /* ── table params state ── */
  const {
    currentPage,
    customPageSize,
    searchValue,
    sortOrder,
    columnName,
    handleSearchChange,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    resetToFirstPage,
  } = useTableQueryParams({ customPageSize: 5, columnName: "id", sortOrder: "desc" });

  const [contactData, setContactData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [companyList, setCompanyList] = useState([]);
  const [positionList, setPositionList] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState("");
  const [openActionId, setOpenActionId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  /* ── Fetch dropdowns ── */
  const fetchCompanyList = useCallback(async () => {
    try {
      const r = await listOfCompanyApi();
      if (checkStatusCodeSuccess(r?.statusCode))
        setCompanyList((r?.data || []).map((i) => ({ label: i.company_name, value: i.id })));
      else setCompanyList([]);
    } catch {
      setCompanyList([]);
    }
  }, []);

  const fetchPositionList = useCallback(async () => {
    try {
      const r = await listOfSalesPosition();
      if (checkStatusCodeSuccess(r?.statusCode))
        setPositionList((r?.data || []).map((i) => ({ label: i.position_name, value: i.id })));
      else setPositionList([]);
    } catch {
      setPositionList([]);
    }
  }, []);

  useEffect(() => {
    fetchCompanyList();
    fetchPositionList();
  }, [fetchCompanyList, fetchPositionList]);

  /* ── Fetch contacts ── */
  const fetchContactList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listOfContactsApi({
        search: searchValue,
        order: [columnName, sortOrder?.toLowerCase()],
        pageSize: customPageSize,
        pageNumber: currentPage,
      });
      if (checkStatusCodeSuccess(r?.statusCode)) {
        setContactData(r?.data?.listOfContacts || []);
        setTotalPages(r?.data?.totalPages || 1);
        setTotalRecords(r?.data?.totalRecordsCount || 0);
        setTotalNumberOfRows(r?.data?.numberOfRows || 0);
      } else setContactData([]);
    } catch {
      setContactData([]);
    } finally {
      setLoading(false);
    }
  }, [searchValue, columnName, sortOrder, customPageSize, currentPage, setLoading]);

  useEffect(() => {
    fetchContactList();
  }, [fetchContactList]);

  /* ── Delete ── */
  const confirmDeleteContact = async () => {
    try {
      setDeleteLoader(true);
      const r = await DeleteContactApi(deleteId);
      if (checkStatusCodeSuccess(r?.statusCode)) {
        toast.success(r?.message);
        setShowDeleteModal(false);
        setDeleteId(null);
        resetToFirstPage();
      } else toast.error(r?.message);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setDeleteLoader(false);
    }
  };

  /* ── Helpers ── */
  const handleCloseAddModal = (resetForm) => {
    setShowAddModal(false);
    setIsEditMode(false);
    setSelectedId(null);
    setFormData(EMPTY_FORM);
    resetForm();
  };

  /* ── Close outside click ── */
  useEffect(() => {
    const h = () => setOpenActionId(null);
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, []);

  /* ── Columns ── */
  const TipCell = (val, max = 28, color = "#6366f1") => {
    const t = truncateText(val, max);
    return val && val.length > max ? (
      <BaseTooltip label={val} position="top" maxWidth={280} color={color}>
        <span style={{ cursor: "default", borderBottom: "1px dashed var(--glass-border)" }}>
          {t}
        </span>
      </BaseTooltip>
    ) : (
      <span>{val || "—"}</span>
    );
  };

  const columns = useMemo(
    () => [
      {
        header: SrNo,
        accessorKey: "serial_number",
        enableColumnFilter: false,
        cell: (cell) => (currentPage - 1) * customPageSize + cell.row.index + 1,
      },
      {
        id: Contact_Name,
        header: ContactHeader,
        accessorKey: Contact_Name,
        enableColumnFilter: false,
        cell: (c) => TipCell(c.row.original?.contact_name, 22, "#10b981"),
      },
      {
        id: email,
        header: Email,
        accessorKey: email,
        enableColumnFilter: false,
        cell: (c) => TipCell(c.row.original?.email, 26, "#06b6d4"),
      },
      {
        id: Contact_No,
        header: ContactNo,
        accessorKey: Contact_No,
        enableColumnFilter: false,
        cell: (c) => TipCell(c.row.original?.contact_no, 18, "#10b981"),
      },
      {
        id: Contact_Location,
        header: Address,
        accessorKey: Contact_Location,
        enableColumnFilter: false,
        cell: (c) => {
          const addr = c.row.original?.contact_location;
          const truncated = truncateText(addr, 30);
          return addr && addr.length > 30 ? (
            <BaseTooltip label={addr} position="top" maxWidth={280} color="#6366f1">
              <span style={{ cursor: "default", borderBottom: "1px dashed var(--glass-border)" }}>
                {truncated}
              </span>
            </BaseTooltip>
          ) : (
            <span>{truncated}</span>
          );
        },
      },
      {
        id: Position_id,
        header: Position,
        accessorKey: Position_id,
        cell: (c) => TipCell(c.row.original?.salesPosition?.position_name, 20, "#8b5cf6"),
        enableColumnFilter: false,
      },
      {
        id: Company_Name,
        header: Company,
        accessorKey: CompanyKey,
        cell: (c) => TipCell(c.row.original?.company?.company_name, 22, "#6366f1"),
        enableColumnFilter: false,
      },
      {
        id: Actions.toLowerCase(),
        header: Actions,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const contact = row.original;
          const isOpen = openActionId === contact.id;
          const actions = [
            {
              label: Remarks,
              icon: Eye,
              color: "#10b981",
              onClick: () => {
                setSelectedRemarks(contact?.remarks || "—");
                setShowRemarksModal(true);
                setOpenActionId(null);
              },
            },
            {
              label: Contactlink,
              icon: ExternalLink,
              color: "#06b6d4",
              disabled: !contact?.contact_link,
              onClick: () => {
                window.open(contact.contact_link, NewTab);
                setOpenActionId(null);
              },
            },
            {
              label: UpdateContact,
              icon: Edit2,
              color: "#f59e0b",
              onClick: () => {
                setIsEditMode(true);
                setSelectedId(contact.id);
                setFormData({
                  company_id: contact?.company?.id || "",
                  contact_name: contact.contact_name || "",
                  email: contact.email || "",
                  contact_no: contact.contact_no || "",
                  contact_location: contact.contact_location || "",
                  position_id: contact.position_id || "",
                  contact_link: contact.contact_link || "",
                  remarks: contact.remarks || "",
                });
                setShowAddModal(true);
                setOpenActionId(null);
              },
            },
            {
              label: Delete,
              icon: Trash2,
              color: "#f43f5e",
              onClick: () => {
                setDeleteId(contact.id);
                setShowDeleteModal(true);
                setOpenActionId(null);
              },
            },
          ];
          return (
            <ActionDropdown
              isOpen={isOpen}
              onToggle={() => setOpenActionId(isOpen ? null : contact.id)}
              onClose={() => setOpenActionId(null)}
              actions={actions}
            />
          );
        },
      },
    ],
    [openActionId, currentPage, customPageSize],
  );

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* ── Page header ── */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "rgba(16,185,129,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Users size={20} strokeWidth={1.75} style={{ color: "#10b981" }} />
          </div>
          <div>
            <h1
              style={{
                fontSize: "1.375rem",
                fontWeight: 800,
                margin: 0,
                color: "var(--text-primary)",
              }}
            >
              {Contacts}
            </h1>
            <p
              style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}
            >
              {totalRecords > 0 ? `${totalRecords} contacts` : "Manage your contacts"}
            </p>
          </div>
        </div>
        <BaseButton
          color="primary"
          onClick={() => {
            setFormData(EMPTY_FORM);
            setIsEditMode(false);
            setSelectedId(null);
            setShowAddModal(true);
          }}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <Plus size={15} strokeWidth={2.5} /> {AddContact}
        </BaseButton>
      </div>

      {/* ── Table ── */}
      <div
        className="animate-slide-up"
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 16,
          overflow: "hidden",
          padding: "16px",
        }}
      >
        {contactData?.length > 0 || searchValue ? (
          <TableContainer
            totalPages={totalPages}
            totalRecords={totalRecords}
            totalNumberOfRows={totalNumberOfRows}
            currentPage={currentPage}
            setCurrentPage={handlePageChange}
            fetchData={handlePageChange}
            customPageSize={customPageSize}
            setCustomPageSize={handlePageSizeChange}
            columns={columns}
            fetchSortingData={handleSortChange}
            data={contactData}
            shouldLineBreak={false}
            onSearch={handleSearchChange}
            manualPagination
            manualFiltering
            SearchPlaceholder={SearchContact}
            isGlobalFilter
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "60px 20px",
              gap: 12,
            }}
          >
            <AlertCircle size={40} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
            <p style={{ color: "var(--text-muted)", margin: 0 }}>No contacts found</p>
          </div>
        )}
      </div>

      {/* ── Add / Edit modal ── */}
      <Formik
        enableReinitialize
        initialValues={formData}
        validationSchema={ContactValidationSchema}
        onSubmit={async (values, { resetForm }) => {
          try {
            setSubmitLoader(true);
            const filtered = Object.fromEntries(
              Object.entries(values).filter(([, v]) => v !== "" && v !== null && v !== undefined),
            );
            const payload = isEditMode ? { id: selectedId, ...filtered } : filtered;
            const r = isEditMode ? await EditContactApi(payload) : await CreateContactApi(payload);
            toast.success(r?.message);
            handleCloseAddModal(resetForm);
            fetchContactList();
          } catch (e) {
            toast.error(e?.response?.data?.message || e.message);
          } finally {
            setSubmitLoader(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          resetForm,
        }) => (
          <BaseModal
            isOpen={showAddModal}
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 9,
                    background: "rgba(99,102,241,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isEditMode ? (
                    <Edit2 size={14} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
                  ) : (
                    <Plus size={14} strokeWidth={2.5} style={{ color: "var(--brand-indigo)" }} />
                  )}
                </div>
                <span>{isEditMode ? UpdateContact : AddContact}</span>
              </div>
            }
            submitText={isEditMode ? Update : Submit}
            submit={handleSubmit}
            disabled={submitLoader}
            loader={submitLoader}
            size="lg"
            toggler={() => !submitLoader && handleCloseAddModal(resetForm)}
          >
            <form>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: "0 16px",
                }}
              >
                <BaseSelect
                  name={Company_Id}
                  label={CompanyLabel}
                  options={companyList}
                  value={values.company_id}
                  handleChange={setFieldValue}
                  touched={touched.company_id}
                  error={touched.company_id && errors.company_id}
                  required
                />
                <BaseInput
                  name={Contact_Name}
                  label={Name}
                  value={values.contact_name}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  placeholder={InputPlaceHolder(ContactPlaceholder)}
                  touched={touched.contact_name}
                  error={touched.contact_name && errors.contact_name}
                  required
                />
                <BaseInput
                  name={email}
                  label={Email}
                  placeholder={InputPlaceHolder(EmailPlaceholder)}
                  value={values.email}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  touched={touched.email}
                  error={touched.email && errors.email}
                />
                <BaseInput
                  type="phone"
                  name={Contact_No}
                  label={ContactNo}
                  placeholder={InputPlaceHolder(ContactNumPlaceholder)}
                  value={values.contact_no}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  touched={touched.contact_no}
                  error={touched.contact_no && errors.contact_no}
                />
                <BaseSelect
                  name={Position_id}
                  label={Position}
                  options={positionList}
                  placeholder={InputPlaceHolder(Position)}
                  value={values.position_id}
                  handleChange={setFieldValue}
                  handleBlur={handleBlur}
                />
                <BaseInput
                  label={Contactlink}
                  name={Contact_Link}
                  placeholder={InputPlaceHolder(Contactlink)}
                  value={values.contact_link}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  touched={touched.contact_link}
                  error={touched.contact_link && errors.contact_link}
                  icon={<Link size={13} strokeWidth={2} />}
                />
                <div style={{ gridColumn: "1 / -1" }}>
                  <BaseInput
                    type="textarea"
                    name={Contact_Location}
                    label={Address}
                    placeholder={InputPlaceHolder(Address)}
                    value={values.contact_location}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                  />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <BaseInput
                    label={Remarks}
                    type="textarea"
                    name={Contact_Remarks}
                    placeholder={InputPlaceHolder(Remarks)}
                    value={values.remarks}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                  />
                </div>
              </div>
            </form>
          </BaseModal>
        )}
      </Formik>

      {/* ── Delete modal ── */}
      <BaseModal
        isOpen={showDeleteModal}
        title={`${Delete} ${ContactHeader}`}
        submit={confirmDeleteContact}
        submitText={Delete}
        disabled={deleteLoader}
        loader={deleteLoader}
        toggler={() => !deleteLoader && setShowDeleteModal(false)}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "4px 0" }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "rgba(244,63,94,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Trash2 size={16} strokeWidth={2} style={{ color: "#f43f5e" }} />
          </div>
          <p
            style={{
              margin: 0,
              fontSize: "0.9375rem",
              color: "var(--text-primary)",
              lineHeight: 1.5,
            }}
          >
            {DeleteConfirmation}
          </p>
        </div>
      </BaseModal>

      {/* ── Remarks modal ── */}
      <BaseModal
        isOpen={showRemarksModal}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "rgba(16,185,129,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Eye size={14} strokeWidth={2} style={{ color: "#10b981" }} />
            </div>
            <span>{Remarks}</span>
          </div>
        }
        toggler={() => setShowRemarksModal(false)}
        hideSubmit
      >
        <div
          style={{
            whiteSpace: "pre-wrap",
            fontSize: "0.9375rem",
            color: "var(--text-primary)",
            lineHeight: 1.7,
            padding: "4px 0",
            minHeight: 60,
          }}
        >
          {selectedRemarks || <span style={{ color: "var(--text-muted)" }}>No remarks added.</span>}
        </div>
      </BaseModal>
    </div>
  );
};

export default Contact;
